/* eslint-disable complexity */
'use client';

import HomeAbout from './HomeAbout/HomeAbout';
import HomeHeroSection from './HomeHeroSection/HomeHeroSection';
import { For, Stack, Text } from '@chakra-ui/react';
import { useHome } from '@/modules/cms';
import type { HomePage } from '@/modules/sanity-studio/types';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { ProductCard, useListProductsQuery } from '@/modules/products';
import { WebContractListProductsQueryParamsSchema } from '@packages/contracts';
import {
	ContentSection, DefaultStyle, PageContainer, ResponsiveAutoGrid,
} from '@packages/wastic-shared';
import React from 'react';

type HomeContainerProps = {
	homePage: HomePage;
};

const useHomeContainer = () => {
	const locale = useLocale() as 'en' | 'th';
	const isThai = locale === 'th';
	const  { handleNavigateToProductDetail } = useHome();
	const router = useRouter();

	const queryParams = WebContractListProductsQueryParamsSchema.parse({
		pageSize: 3,
		sortBy:   'restockedAt|desc',
	});
	const { data: featureProducts } = useListProductsQuery(queryParams);

	return {
		locale,
		handleNavigateToProductDetail,
		router,
		featureProducts: featureProducts?.products
			? featureProducts.products.map(product => ({
				id:         product.id,
				name:       product.name,
				slug:       product.slug,
				coverImage: product.coverImage,
				price:      {
					amount:     isThai ? product.price : product.priceUsd,
					discounted: isThai ? product.discount?.discountPrice : product.discount?.discountPriceUsd,
					currency:   isThai ? 'THB' : 'USD',
				} as const,
				collection: {
					name: product.collectionName,
					slug: product.collectionSlug,
				},
			}))
			: [],
	};
};

const HomeContainer = ({ homePage }: HomeContainerProps) => {
	const {  heroBanner, aboutUs } = homePage;
	const t = useTranslations('HomePage');
	const {
		locale, handleNavigateToProductDetail, router, featureProducts,
	} = useHomeContainer();

	return (
		<React.Fragment>
			<HomeHeroSection
				cta={heroBanner.cta?.[locale] ?? ''}
				heading={heroBanner.title[locale] ?? ''}
				image={heroBanner.image?.asset.url ?? ''}
				imageMobile={heroBanner.imageMobile?.asset.url ?? ''}
				subheading={heroBanner.description?.[locale] ?? ''}
				onClickCta={() => router.push(heroBanner.link ?? '')}
			/>
			<PageContainer>
				<ContentSection display={'flex'} flexDirection={'column'} gap={DefaultStyle.CONTENT_GAP}>
					<HomeAbout
						description={aboutUs.description[locale] as never}
						title={aboutUs.title[locale] ?? ''}
						youtubeUrl={aboutUs.youtubeUrl ?? ''}
					/>
					<Stack gap={DefaultStyle.TIGHT_SECTION_GAP} w={'100%'}>
						<Text textStyle={'h3'}>{t('featureProduct.title')}</Text>
						<ResponsiveAutoGrid gap={DefaultStyle.GRID_GAP}>
							<For each={featureProducts}>
								{(featureProduct, index) => (
									<ProductCard
										key={`feature-product-${index}`}
										aspectRatio={'landscape'}
										product={featureProduct}
										onClickProductCard={() => handleNavigateToProductDetail(
											featureProduct.collection.slug, featureProduct.slug,
										)}
									/>
								)}
							</For>
						</ResponsiveAutoGrid>
					</Stack>
				</ContentSection>
			</PageContainer>
		</React.Fragment>
	);
};

export default HomeContainer;